// Terms service for Turn Connect API - handles EULA and terms acceptance
import { webApiClient, unwrap } from './apiClient';

export interface TermsDocument {
  id: string;
  type: string; // e.g., "eula", "privacy_policy", "tos", "cookie_policy"
  version: string;
  title: string;
  content_url?: string;
  effective_date: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface PendingTermsResponse {
  has_pending: boolean;
  pending_terms: TermsDocument[];
}

export interface AcceptTermsResponse {
  id: string;
  user_id: string;
  terms_document_id: string;
  accepted_at: string;
  ip_address?: string;
  user_agent?: string;
  terms_document?: TermsDocument;
}

class TermsService {
  /**
   * Get list of pending terms that the current user needs to accept
   */
  async getPendingTerms(): Promise<PendingTermsResponse> {
    return unwrap(webApiClient.get<PendingTermsResponse>('/terms/pending'), 'Failed to fetch pending terms');
  }

  /**
   * Get the HTML content of a terms document by type (e.g., 'eula')
   * Content is served from static frontend assets
   */
  async getTermsContent(type: string = 'eula'): Promise<string> {
    // Map terms types to their static content paths
    const contentPaths: Record<string, string> = {
      eula: '/legal/eula.html',
      privacy_policy: '/legal/privacy-policy.html',
      tos: '/legal/tos.html',
      cookie_policy: '/legal/cookie-policy.html',
    };

    const path = contentPaths[type];
    if (!path) {
      throw new Error(`Unknown terms type: ${type}`);
    }

    // Get the base path from homepage setting (handles /web or /turn-connect/web paths)
    const homepage = process.env.REACT_APP_HOMEPAGE || '';
    const basePath = homepage.replace(/\/$/, ''); // Remove trailing slash

    // TODO(webApiClient): this fetches a static frontend asset relative to the
    // app's homepage base path, not the API base URL — the central client
    // would prepend the API base URL, so this stays a raw fetch.
    // eslint-disable-next-line no-restricted-globals -- fetches a static frontend asset relative to the homepage base, not the API; see TODO(webApiClient) above
    const response = await fetch(`${basePath}${path}`, {
      method: 'GET',
      headers: {
        Accept: 'text/html',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch terms content');
    }

    return response.text();
  }

  /**
   * Get the current active terms document for a specific type
   */
  async getCurrentTerms(type: string): Promise<TermsDocument> {
    return unwrap(webApiClient.get<TermsDocument>(`/terms/${type}/current`), 'Failed to fetch current terms');
  }

  /**
   * Accept a specific terms document
   */
  async acceptTerms(termsId: string): Promise<AcceptTermsResponse> {
    const res = await webApiClient.post<AcceptTermsResponse>(`/terms/${termsId}/accept`);

    if (!res.success) {
      // Preserve structured error semantics: message first, then code.
      const errorBody = res.errorBody as { message?: string; code?: string } | undefined;
      throw new Error(errorBody?.message || errorBody?.code || res.error || 'Failed to accept terms');
    }

    return res.data as AcceptTermsResponse;
  }
}

export const termsService = new TermsService();
